import React, { useCallback, useMemo } from 'react';
import { Box, Divider, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

export type GuidanceSection = {
  title: string;
  description?: React.ReactNode;
  bullets?: Array<string | React.ReactNode>;
  footnote?: React.ReactNode;
};

export type GuidanceAnyItem =
  | { kind: 'title'; content: React.ReactNode | React.ReactNode[] }
  | { kind: 'description'; content: React.ReactNode | React.ReactNode[] }
  | { kind: 'bullets'; items: (string | React.ReactNode)[] }
  | { kind: 'divider' };

export interface GuidanceContentProps {
  heading?: string;
  items: GuidanceAnyItem[];
}

const bulletIconSx = { fontSize: 10, mt: 0.75, color: 'primary.main' } as const;

const StructuredGuidance: React.FC<GuidanceContentProps> = ({ heading, items }) => {
  const renderTitle = useCallback(
    (content: React.ReactNode | React.ReactNode[], key: React.Key) => {
      if (Array.isArray(content)) {
        return content.map((c, i) => (
          <Typography key={i} variant='h5' sx={{ fontWeight: 600, mb: 1.5 }}>
            {c}
          </Typography>
        ));
      }
      return (
        <Typography key={key} variant='h5' sx={{ fontWeight: 600, mb: 1.5 }}>
          {content}
        </Typography>
      );
    },
    []
  );

  const renderDescription = useCallback(
    (content: React.ReactNode | React.ReactNode[], key: React.Key, nextIsBullets: boolean) => {
      const mb = nextIsBullets ? 2.5 : 0;
      if (Array.isArray(content)) {
        return content.map((c, i) => (
          <Typography key={i} variant='body1' sx={{ mb }}>
            {c}
          </Typography>
        ));
      }
      return (
        <Typography key={key} variant='body1' sx={{ mb }}>
          {content}
        </Typography>
      );
    },
    []
  );

    const renderBullets = useCallback(
      (bullets: (string | React.ReactNode)[], key: React.Key) => (
        <List key={key} disablePadding>
          {bullets.map((bullet, bulletIndex) => {
            let bulletKey: string = String(bulletIndex);
            if (typeof bullet === 'string') {
              bulletKey = `${bullet}-${bulletIndex}`;
            } else if (React.isValidElement(bullet) && bullet.key != null) {
              bulletKey = `${bullet.key}-${bulletIndex}`;
            } else if (typeof bullet === 'object' && bullet !== null) {
              bulletKey = `${JSON.stringify(bullet)}-${bulletIndex}`;
            }
            return (
              <ListItem key={bulletKey} sx={{ alignItems: 'flex-start', pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 26 }}>
                  <FiberManualRecordIcon sx={bulletIconSx} />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ variant: 'body1', sx: { lineHeight: 1.6 } }}
                  primary={bullet}
                />
              </ListItem>
            );
          })}
        </List>
      ),
      []
    );

  const renderedItems = useMemo(
    () =>
      items.map((item, idx) => {
        switch (item.kind) {
          case 'divider':
            return <Divider key={idx} sx={{ my: 3 }} />;
          case 'title':
            return <Box key={idx}>{renderTitle(item.content, idx)}</Box>;
          case 'description': {
            const next = items[idx + 1];
            const nextIsBullets = !!next && next.kind === 'bullets';
            return <Box key={idx}>{renderDescription(item.content, idx, nextIsBullets)}</Box>;
          }
          case 'bullets':
            return (
              <Box key={idx} sx={{ mb: 2 }}>
                {renderBullets(item.items, idx)}
              </Box>
            );
          default:
            return null;
        }
      }),
    [items, renderTitle, renderDescription, renderBullets]
  );

  const headingNode = useMemo(
    () =>
      heading ? (
        <Typography variant='h4' sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
          {heading}
        </Typography>
      ) : null,
    [heading]
  );

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      {headingNode}
      {renderedItems}
    </Box>
  );
};

export default StructuredGuidance;
